import api from '../../api/apiService';
import { PermissionEnum, PermissionDto, UserPermissions, UserSubscription } from '../../models/Permissions';
import { getCurrentUser } from '../auth';

const moduleUri = (userId: string): string => `/entitlements/v1/users/${userId}/effective-permissions`;

let permissionsPromise: Promise<string[]>;

export const fetchEffectivePermissions = (): Promise<string[]> => {
  const { userId } = getCurrentUser();
  if (!userId) {
    throw new Error('Failed to get userId for permissions');
  }
  permissionsPromise = api.get<PermissionDto[]>(moduleUri(userId)).then((response) => {
    return (response.data || []).map((permission) => permission.code);
  });
  return permissionsPromise;
};

const isInvalidPermissions = (permissions) => {
  const hasAtLeastOneUiPermission =
    permissions.includes(PermissionEnum.RESEARCH) ||
    permissions.includes(PermissionEnum.NEWS) ||
    permissions.includes(PermissionEnum.COVENANTS);
  return !hasAtLeastOneUiPermission;
};

const resolveSubscription = (permissions: string[]): UserSubscription | null => {
  const hasResearchPermissions = permissions.includes(PermissionEnum.RESEARCH);
  const hasCovenantsPermissions = permissions.includes(PermissionEnum.COVENANTS);
  const hasNewsPermissions = permissions.includes(PermissionEnum.NEWS);

  if (hasResearchPermissions && hasCovenantsPermissions && hasNewsPermissions) {
    return UserSubscription.CS_CR_LFI;
  }
  if (hasResearchPermissions && hasCovenantsPermissions) {
    return UserSubscription.CS_CR;
  }
  if (hasResearchPermissions && hasNewsPermissions) {
    return UserSubscription.CS_LFI;
  }
  if (hasResearchPermissions) {
    return UserSubscription.CS;
  }
  if (hasCovenantsPermissions && hasNewsPermissions) {
    return UserSubscription.CR_LFI;
  }
  if (hasCovenantsPermissions) {
    return UserSubscription.CR;
  }
  if (hasNewsPermissions) {
    return UserSubscription.LFI;
  }
  return null;
};

export const getPermissions = async (): Promise<UserPermissions> => {
  const { userId } = getCurrentUser();
  if (!userId) {
    return Promise.reject('Failed to get userId for permissions');
  }
  try {
    const permissions = await permissionsPromise;
    if (isInvalidPermissions(permissions)) {
      return Promise.reject('Incorrect permissions');
    }
    const subscriptions = resolveSubscription(permissions)!;
    return new UserPermissions(permissions, subscriptions);
  } catch (e) {
    return Promise.reject('Failed to get permissions');
  }
};
